import globalMaterialUITheme from '@alo/yoga-ui';
import { Grid, ThemeProvider, createMuiTheme } from '@material-ui/core';
import React, { useMemo, useEffect } from 'react';
import { PlpTile } from 'src/scripts/templates/product-listing-page/components/PlpTiles';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { ProductNode } from 'src/scripts/templates/product-listing-page/types';
import {
  getPlpTileCompliantProductNodes,
  getAloSuggestedProducts,
  truncateSearchText,
  trackRecentSearch,
} from '../utils';
import './style.scss';
import { checkIfCustomer, encodeURIComponentFormWWW } from '../../../../utils/helpers';
import { prependLocaleToPath } from '../../../../utils/localization';

const ViewAllResultsLink = ({ searchText }: { searchText: string }) => {
  // trim search query so it can't be too long in the UI
  const trimmedSearchString = truncateSearchText(searchText, 30);
  const searchUrl = prependLocaleToPath(`/search?q=${encodeURIComponentFormWWW(searchText)}`);
  return (
    <a
      href={searchUrl}
      className="view-all-results"
      onClick={(event) => {
        event.preventDefault();
        trackRecentSearch(searchText);
        window.location.href = searchUrl;
      }}
    >
      View All Results for "<span>{trimmedSearchString}</span>"
    </a>
  );
};

const PopularProducts = ({
  productItems,
  searchText,
  isMobileVersion,
  isEnglishUser,
}: {
  productItems: ProductNode[] | undefined;
  searchText?: string;
  isMobileVersion?: boolean;
  isEnglishUser: boolean;
}) => {
  const customMUITheme = createMuiTheme({
    ...globalMaterialUITheme,
    spacing: 6,
  });

  let header = 'Popular Products';
  let linkTrackingClass = 'generic';
  const shouldShowSearchResults = productItems && productItems?.length > 0 && isEnglishUser;
  const [suggestedProducts, setSuggestedProducts] = React.useState<ProductNode[] | null>();

  useEffect(() => {
    if (!shouldShowSearchResults && window.AloSearchSettings && window.AloSearchSettings.suggestedProducts) {
      const aloSuggestedProducts = getAloSuggestedProducts(window.AloSearchSettings.suggestedProducts);
      (async () => {
        const plpTileCompliantSuggestedProductNodes = await getPlpTileCompliantProductNodes(aloSuggestedProducts);
        setSuggestedProducts(plpTileCompliantSuggestedProductNodes);
      })();
    }
    // eslint-disable-next-line
  }, []);

  if (shouldShowSearchResults && isEnglishUser) {
    header = 'Recommended Products';
    linkTrackingClass = 'tailored';
  }

  const isCustomer = useMemo(() => {
    return checkIfCustomer();
  }, []);

  const isWishlist = window.location.href.includes('view=wishlist');

  return (productItems && productItems.length) || (suggestedProducts && suggestedProducts.length) ? (
    <ThemeProvider theme={customMUITheme}>
      <Grid id="popular-products-list" className={'popular-products-list ' + linkTrackingClass} role="list">
        <Grid className="popular-search d-flex">
          <h6 className="header-title h6">{header}</h6>
          {searchText && shouldShowSearchResults && <ViewAllResultsLink searchText={searchText} />}
          <div className="product-cards">
            <ErrorBoundary>
              {shouldShowSearchResults ? (
                <>
                  {productItems?.slice(0, 4).map((product: ProductNode, index: number) => {
                    return (
                      <PlpTile
                        productData={product}
                        index={index}
                        isCustomer={isCustomer}
                        isWishlist={isWishlist}
                        collectionHandle={'search-page-drawer'}
                        collectionShopifyId={''}
                        hasAFilterSelected={false}
                        source="Search Drawer"
                        shouldHideBuilderContent={true}
                      />
                    );
                  })}
                </>
              ) : (
                suggestedProducts?.slice(0, 4).map((product: ProductNode, index: number) => {
                  return (
                    <PlpTile
                      productData={product}
                      index={index}
                      isCustomer={isCustomer}
                      isWishlist={isWishlist}
                      collectionHandle={'search-page-drawer'}
                      collectionShopifyId={''}
                      hasAFilterSelected={false}
                      source="Search Drawer"
                      shouldHideBuilderContent={true}
                    />
                  );
                })
              )}
            </ErrorBoundary>
            {searchText && shouldShowSearchResults && isMobileVersion && <ViewAllResultsLink searchText={searchText} />}
          </div>
        </Grid>
      </Grid>
    </ThemeProvider>
  ) : null;
};
export default PopularProducts;
